import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { environment } from 'environments/environment';

export interface VoiceTranscription {
  transcript: string;
  isFinal: boolean;
  confidence?: number;
}

export interface AzureSpeechConfig {
  subscriptionKey: string;
  region: string;
  language?: string;
}

export interface VoiceRecognitionConfig {
  language?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
  azureConfig?: AzureSpeechConfig;
}

@Injectable({
  providedIn: 'root'
})
export class VoiceToTextService {
  private _recognition: any;
  private _azureRecognizer: SpeechSDK.SpeechRecognizer | null = null;
  private readonly _isListening$ = new BehaviorSubject<boolean>(false);
  private readonly _transcription$ = new Subject<VoiceTranscription>();
  private readonly _error$ = new Subject<string>();
  private _useAzure = false;

  constructor() {
    this._initializeBrowserRecognition();
  }

  get isListening$(): Observable<boolean> {
    return this._isListening$.asObservable();
  }

  get transcription$(): Observable<VoiceTranscription> {
    return this._transcription$.asObservable();
  }

  get error$(): Observable<string> {
    return this._error$.asObservable();
  }

  get isListening(): boolean {
    return this._isListening$.value;
  }

  /**
   * Start listening for voice input
   */
  startListening(config?: VoiceRecognitionConfig): void {
    if (this._isListening$.value) {
      return;
    }

    if (config?.azureConfig) {
      this._useAzure = true;
      this._startAzureRecognition(config);
    } else {
      this._useAzure = false;
      this._startBrowserRecognition(config);
    }
  }

  /**
   * Start listening using app-level environment configuration
   */
  startListeningFromConfiguration(): void {
    const voiceConfig = environment.voiceRecognition;

    if (voiceConfig.provider === 'azure') {
      if (!voiceConfig.azure?.subscriptionKey || voiceConfig.azure.subscriptionKey.trim() === '') {
        this._error$.next('Azure Speech is configured but subscription key is missing. Please check environment settings.');
        return;
      }

      this.startListening({
        language: voiceConfig.azure.language || 'en-US',
        continuous: true,
        interimResults: true,
        azureConfig: {
          subscriptionKey: voiceConfig.azure.subscriptionKey,
          region: voiceConfig.azure.region || 'eastus',
          language: voiceConfig.azure.language || 'en-US'
        }
      });
      return;
    }

    if (!this.isBrowserSpeechRecognitionSupported()) {
      this._error$.next('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    this.startListening({
      language: 'en-US',
      continuous: true,
      interimResults: true,
      maxAlternatives: 1
    });
  }

  /**
   * Stop listening for voice input
   */
  stopListening(): void {
    if (!this._isListening$.value) {
      return;
    }

    if (this._useAzure) {
      this._stopAzureRecognition();
    } else {
      this._stopBrowserRecognition();
    }
  }

  /**
   * Toggle listening state
   */
  toggleListening(config?: VoiceRecognitionConfig): void {
    if (this._isListening$.value) {
      this.stopListening();
    } else {
      this.startListening(config);
    }
  }

  /**
   * Check if browser supports speech recognition
   */
  isBrowserSpeechRecognitionSupported(): boolean {
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }

  /**
   * Initialize browser-based speech recognition
   */
  private _initializeBrowserRecognition(): void {
    if (!this.isBrowserSpeechRecognitionSupported()) {
      console.warn('Browser speech recognition not supported');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    this._recognition = new SpeechRecognition();
  }

  /**
   * Start browser-based speech recognition
   */
  private _startBrowserRecognition(config?: VoiceRecognitionConfig): void {
    if (!this._recognition) {
      this._error$.next('Speech recognition not supported in this browser');
      return;
    }

    // Configure recognition
    this._recognition.continuous = config?.continuous ?? true;
    this._recognition.interimResults = config?.interimResults ?? true;
    this._recognition.lang = config?.language ?? 'en-US';
    this._recognition.maxAlternatives = config?.maxAlternatives ?? 1;

    // Handle results
    this._recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const isFinal = result.isFinal;
        const confidence = result[0].confidence;

        this._transcription$.next({
          transcript,
          isFinal,
          confidence
        });
      }
    };

    // Handle errors
    this._recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);

      // Map error types to user-friendly messages
      let errorMessage = 'An error occurred during speech recognition';
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found. Please check your microphone connection.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone permission denied. Please allow microphone access.';
          break;
        case 'network':
          errorMessage = 'Network error occurred. Please check your connection.';
          break;
        case 'aborted':
          errorMessage = 'Speech recognition was aborted.';
          break;
      }

      this._error$.next(errorMessage);
      this._isListening$.next(false);
    };

    // Handle end of recognition
    this._recognition.onend = () => {
      this._isListening$.next(false);
    };

    // Handle start of recognition
    this._recognition.onstart = () => {
      this._isListening$.next(true);
    };

    // Start recognition
    try {
      this._recognition.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      this._error$.next('Failed to start speech recognition');
      this._isListening$.next(false);
    }
  }

  /**
   * Stop browser-based speech recognition
   */
  private _stopBrowserRecognition(): void {
    if (this._recognition) {
      try {
        this._recognition.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
    }
    this._isListening$.next(false);
  }

  /**
   * Start Azure Speech recognition
   */
  private _startAzureRecognition(config: VoiceRecognitionConfig): void {
    if (!config.azureConfig) {
      this._error$.next('Azure Speech configuration is required');
      return;
    }

    try {
      const { subscriptionKey, region, language } = config.azureConfig;

      // Create speech configuration
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(subscriptionKey, region);
      speechConfig.speechRecognitionLanguage = language || config.language || 'en-US';

      // Create audio configuration
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();

      // Create recognizer
      this._azureRecognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

      // Set up event handlers
      this._azureRecognizer.recognizing = (s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
          this._transcription$.next({
            transcript: e.result.text,
            isFinal: false,
            confidence: 0
          });
        }
      };

      this._azureRecognizer.recognized = (s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          this._transcription$.next({
            transcript: e.result.text,
            isFinal: true,
            confidence: 1
          });
        } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
          console.log('No speech could be recognized');
        }
      };

      this._azureRecognizer.canceled = (s, e) => {
        console.error('Azure Speech recognition canceled:', e.errorDetails);

        let errorMessage = 'Speech recognition was canceled';
        if (e.errorCode === SpeechSDK.CancellationErrorCode.AuthenticationFailure) {
          errorMessage = 'Authentication failed. Please check your Azure credentials.';
        } else if (e.errorCode === SpeechSDK.CancellationErrorCode.ConnectionFailure) {
          errorMessage = 'Connection failed. Please check your network connection.';
        } else if (e.errorDetails) {
          errorMessage = e.errorDetails;
        }

        this._error$.next(errorMessage);
        this._isListening$.next(false);
      };

      this._azureRecognizer.sessionStopped = (s, e) => {
        this._isListening$.next(false);
      };

      // Start continuous recognition
      if (config.continuous) {
        this._azureRecognizer.startContinuousRecognitionAsync(
          () => {
            this._isListening$.next(true);
          },
          error => {
            console.error('Failed to start Azure recognition:', error);
            this._error$.next('Failed to start speech recognition');
            this._isListening$.next(false);
          }
        );
      } else {
        this._azureRecognizer.recognizeOnceAsync(
          result => {
            this._isListening$.next(true);
            if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
              this._transcription$.next({
                transcript: result.text,
                isFinal: true,
                confidence: 1
              });
            }
            this._isListening$.next(false);
          },
          error => {
            console.error('Failed to start Azure recognition:', error);
            this._error$.next('Failed to start speech recognition');
            this._isListening$.next(false);
          }
        );
      }
    } catch (error) {
      console.error('Error initializing Azure Speech recognition:', error);
      this._error$.next('Failed to initialize speech recognition');
      this._isListening$.next(false);
    }
  }

  /**
   * Stop Azure Speech recognition
   */
  private _stopAzureRecognition(): void {
    if (this._azureRecognizer) {
      try {
        this._azureRecognizer.stopContinuousRecognitionAsync(
          () => {
            this._isListening$.next(false);
            this._azureRecognizer?.close();
            this._azureRecognizer = null;
          },
          error => {
            console.error('Error stopping Azure recognition:', error);
            this._isListening$.next(false);
            this._azureRecognizer?.close();
            this._azureRecognizer = null;
          }
        );
      } catch (error) {
        console.error('Error stopping Azure recognition:', error);
        this._isListening$.next(false);
        if (this._azureRecognizer) {
          this._azureRecognizer.close();
          this._azureRecognizer = null;
        }
      }
    } else {
      this._isListening$.next(false);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopListening();

    if (this._azureRecognizer) {
      this._azureRecognizer.close();
      this._azureRecognizer = null;
    }

    this._isListening$.complete();
    this._transcription$.complete();
    this._error$.complete();
  }
}
